/**
 * Simple Weighted Scoring tool runner.
 * Scores items using weighted criteria and calculates weighted totals.
 */

import type {
  Item,
  CriteriaSet,
  Criterion,
  ToolConfig,
  Lens,
} from "../harmonizer/types";
import { readToolSettings } from "../lib/toolSettings";
import type { NativeScoreSet } from "../harmonizer/testHarness";
import type { RawToolSpec } from "../harmonizer/specLoader";
import { loadToolSpecFile, getToolSpecPath } from "./specLoader";

let cachedSpec: RawToolSpec | null = null;

/**
 * Gets the raw tool spec for Simple Weighted Scoring.
 */
export function getSimpleWeightedScoringSpec(): RawToolSpec {
  if (cachedSpec) {
    return cachedSpec;
  }

  const specPath = getToolSpecPath("simple-weighted-scoring");
  cachedSpec = loadToolSpecFile(specPath);
  return cachedSpec;
}

/**
 * Runs the Simple Weighted Scoring tool.
 * 
 * @param items - Items to score
 * @param criteriaSet - CriteriaSet (required, must contain criteria with weights)
 * @param toolConfig - Tool configuration (scoring_scale_min, scoring_scale_max, require_all_scores)
 * @param lens - Optional lens for filtering
 * @returns Native score set
 */
export async function runSimpleWeightedScoringTool(
  items: Item[],
  criteriaSet: CriteriaSet | null,
  toolConfig: ToolConfig,
  lens?: Lens | null
): Promise<NativeScoreSet> {
  const toolRunId = `simple-weighted-scoring-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  if (!criteriaSet || !criteriaSet.criteria || criteriaSet.criteria.length === 0) {
    throw new Error("Simple Weighted Scoring requires a CriteriaSet with at least one criterion");
  }

  // Get config values
  // The scale the session chose at setup (lib/toolSettings): the same reader
  // the scoring screen uses, so a score the screen offered is never dropped
  // here as out of range.
  const { min: scaleMin, max: scaleMax } = readToolSettings(toolConfig).scoringScale;
  const requireAllScores = (toolConfig.require_all_scores as boolean) ?? true;

  // Filter items if lens is provided
  let filteredItems = items;
  if (lens) {
    const { applyLens } = await import("../arrange/validation");
    filteredItems = applyLens(items, lens);
  }

  // Extract criteria and weights
  // Note: In the TOOL_SPEC, criteria have a 'weight' field (0-100)
  // We need to extract this from the criteria metadata or assume it's in a weight field
  const criteriaMap = new Map<string, { id: string; name: string; weight: number }>();
  let totalWeight = 0;

  for (const criterion of criteriaSet.criteria) {
    // Weight might be in criterion metadata or as a separate field
    // For now, we'll look for it in a weight attribute or assume equal weights
    // Note: `Criterion` (harmonizer/types.ts) has no `weight` field today; this
    // reads an optional extension property some criteria carry at runtime.
    // `??`, not `||`: a criterion deliberately weighted 0 is not the same as one
    // carrying no weight at all, and `||` silently reinstated it at the full
    // equal share — the one dimension a session had chosen to exclude came back
    // weighing as much as the ones it kept.
    const declaredWeight = (criterion as Criterion & { weight?: number }).weight;
    const weight =
      typeof declaredWeight === "number" && Number.isFinite(declaredWeight)
        ? declaredWeight
        : 100 / criteriaSet.criteria.length;
    criteriaMap.set(criterion.id, {
      id: criterion.id,
      name: criterion.name,
      weight: weight,
    });
    totalWeight += weight;
  }

  // Normalize weights to sum to 100 if needed
  if (totalWeight !== 100 && totalWeight > 0) {
    for (const crit of criteriaMap.values()) {
      crit.weight = (crit.weight / totalWeight) * 100;
    }
  }

  const scoredItems: Array<{
    item_id: string;
    weighted_total: number;
    rank: number;
    criterion_contributions: Record<
      string,
      { score: number; weighted_contribution: number }
    >;
  }> = [];
  const itemsWithScores: Array<{
    item: Item;
    weightedTotal: number;
    criterionContributions: Record<string, { score: number; weighted_contribution: number }>;
  }> = [];

  for (const item of filteredItems) {
    let hasAllScores = true;
    const criterionContributions: Record<string, { score: number; weighted_contribution: number }> = {};
    let weightedTotal = 0;

    // Check scores for each criterion
    for (const [criterionId, criterion] of criteriaMap.entries()) {
      const score = item.attributes[criterionId];

      if (score === null || score === undefined) {
        if (requireAllScores) {
          hasAllScores = false;
          break;
        } else {
          continue; // Skip this criterion for this item
        }
      }

      const scoreNum = typeof score === "number" ? score : Number(score);

      // Validate score is within scale
      if (scoreNum < scaleMin || scoreNum > scaleMax) {
        if (requireAllScores) {
          hasAllScores = false;
          break;
        } else {
          continue;
        }
      }

      // Calculate weighted contribution
      const weightedContribution = (scoreNum * criterion.weight) / 100;
      weightedTotal += weightedContribution;

      criterionContributions[criterionId] = {
        score: scoreNum,
        weighted_contribution: weightedContribution,
      };
    }

    if (hasAllScores || !requireAllScores) {
      itemsWithScores.push({
        item,
        weightedTotal,
        criterionContributions,
      });
    }
  }

  // Sort by weighted total (descending), then by label (ascending) for tie-breaking
  itemsWithScores.sort((a, b) => {
    if (b.weightedTotal !== a.weightedTotal) {
      return b.weightedTotal - a.weightedTotal;
    }
    return a.item.label.localeCompare(b.item.label);
  });

  // Assign ranks and build output
  let currentRank = 1;
  for (let i = 0; i < itemsWithScores.length; i++) {
    const { item, weightedTotal, criterionContributions } = itemsWithScores[i];

    // If score differs from previous, update rank
    if (i > 0 && itemsWithScores[i - 1].weightedTotal !== weightedTotal) {
      currentRank = i + 1;
    }

    scoredItems.push({
      item_id: item.id,
      weighted_total: weightedTotal,
      rank: currentRank,
      criterion_contributions: criterionContributions,
    });
  }

  return {
    items: scoredItems,
    toolRunId,
    metadata: {
      total_items: filteredItems.length,
      valid_items: scoredItems.length,
      criteria_set_id: criteriaSet.id,
      scoring_scale: { min: scaleMin, max: scaleMax },
      total_criteria: criteriaSet.criteria.length,
    },
  };
}


